import { formatStartedAt, useAppVersion } from '@/shared/hooks/useAppVersion'

/**
 * 一行「标签 + 值」。
 * always=true 时即使值为空也渲染（版本号缺失要显示 unknown，不能让用户看到空白）；
 * 否则值为空即隐藏整行（部署分支 / commit 缺失时用）。
 */
const InfoRow: React.FC<{ label: string; value?: string; always?: boolean }> = ({
  label,
  value,
  always = false,
}) => {
  if (!always && !value) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '1.5rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '0.9375rem',
      }}
    >
      <span style={{ flex: '0 0 8rem', color: '#64748b' }}>{label}</span>
      <span style={{ flex: 1, color: '#1e293b', fontWeight: 600, wordBreak: 'break-all' }}>
        {value}
      </span>
    </div>
  )
}

/**
 * 版本信息块 —— 部署版本 / 分支 / commit / 服务启动时间
 *
 * 两处入口共用这份内容，避免两边信息漂移：
 * - 「关于」页面（`frontend/src/pages/AboutPage.tsx`，入口在侧边栏用户菜单）
 * - 「关于」弹窗（`AboutDialog.tsx`，入口在侧边栏左下角 / 登录页 / 用户指南的版本号）
 *
 * 只放版本相关的行——系统名称与描述不在这里出现，标题由各自的载体负责
 * （页面有 `<h1>`、弹窗有「关于」表头）。
 *
 * 数据来自 useAppVersion（GET /api/version）；接口不可用时按 hook 内的兜底
 * 显示 unknown，不报错、不影响页面其它内容。详见 docs/version_management_design.md 第 7 节
 */
const AboutInfo: React.FC = () => {
  const appVersion = useAppVersion()

  return (
    <div>
      <InfoRow label="部署版本" value={appVersion.version} always />
      <InfoRow label="部署分支" value={appVersion.branch} />
      <InfoRow label="部署 commit" value={appVersion.commit} />
      {/* 「服务启动时间」而非「部署时间」：这只是后端进程起来的时刻，
          容器重启就会刷新；真正的版本发布时间看「部署版本」里的时间戳 */}
      <InfoRow label="服务启动时间" value={formatStartedAt(appVersion.startedAt)} />
    </div>
  )
}

export default AboutInfo
